import React, { useEffect, useRef, useState } from "react";
import { ListCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import { utils, write } from "xlsx";
import { saveAs } from "file-saver";
import { roles } from "utils/constant";
import {
  ArrowDownIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
  DocumentDuplicateIcon,
  EnvelopeIcon,
  DocumentArrowDownIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";
import ViewPickupMail from "./ViewPickupMail";
import { Button, Checkbox, Popover, PopoverTrigger, PopoverContent } from "@heroui/react";
import Spinner from "utils/Spinner";

// Compact select styles (ToPayPickup style)
const pickupSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "0px 2px",
    minHeight: "32px",
    fontSize: "12px",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#0077B6" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 6px" }),
  input: (base: any) => ({ ...base, margin: "0px", padding: "0px" }),
  indicatorsContainer: (base: any) => ({ ...base, height: "32px" }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b",
    fontSize: "12px",
    padding: "4px 10px",
    cursor: "pointer",
  }),
  menu: (base: any) => ({ ...base, borderRadius: "10px", overflow: "hidden", boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)", zIndex: 9999 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "12px" }),
  singleValue: (base: any) => ({ ...base, color: "#1e293b", fontSize: "12px" }),
};

const STORAGE_KEY = "creditReportVisibleColumns";

const CreditReport: React.FC<any> = (props) => {
  const [tableData, setTableData] = useState<any>([]);
  const [totals, setTotals] = useState<any>({});
  const [startDate, setStartDate] = useState<any>(moment().startOf("day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<any>(moment().endOf("day").format("YYYY-MM-DD"));

  const [query, setQuery] = useState<any>({
    date: {
      startDate: moment().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
      endDate: moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    },
  });
  const [isDataAdded, setIsDataAdded] = useState(false);

  const [selectedCustomer, setBranchOtp] = useState<any>();
  const [customerList, setcustomerList] = useState<any>([{ value: 0, label: "Loading..." }]);

  const [selectedProduct, setSelectedProduct] = useState<any>();
  const [productList, setProductList] = useState<any>([]);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [allColumnKeys, setAllColumnKeys] = useState<string[]>([]);
  const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);

  const [sortStat, setSortStat] = useState<any>({});
  const [isPopupOpen, setIsPopupOpen] = useState("");

  // Load saved column visibility from localStorage
  const loadSavedColumns = (keys: string[]) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const valid = parsed.filter((k: string) => keys.includes(k));
        if (valid.length > 0) return valid;
      }
    } catch (e) {}
    return keys;
  };

  useEffect(() => {
    if (visibleColumns.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
      } catch (e) {}
    }
  }, [visibleColumns]);

  const toggleColumnVisibility = (key: string) => {
    setVisibleColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const isColumnVisible = (key: string) => {
    if (key === "Customer" && selectedCustomer) return false;
    return visibleColumns.includes(key);
  };

  const selectAllColumns = () => setVisibleColumns([...allColumnKeys]);

  // Fetch product list
  useQuery(["ProductListApi"], () => ListCommanWay("/admin/product/list", {}, { paginate: 1000 }), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS" && data.data?.data && Array.isArray(data.data.data)) {
        const opts = data.data.data.map((p: any) => ({
          value: p.product_code,
          label: p.product_name,
        }));
        setProductList(opts);
      }
    },
  });

  const {
    isFetching: branchIsFetching,
    isError: branchIsError,
    isFetched: branchIsFetched,
    isLoading: branchIsLoading,
    refetch: branchRefetch,
  } = useQuery(["CustomersListApi"], () => ListCommanWay("/admin/customer/list", { isActive: true }, { paginate: 1000 }), {
    keepPreviousData: true,
    onSuccess(data) {
      if (data.status == "SUCCESS" && data.data?.data && Array.isArray(data.data.data)) {
        let opt = [];
        data.data.data.forEach((p) => {
          opt.push({
            value: p.code,
            label: p.code + " - " + p.sh,
          });
        });
        setcustomerList(opt);
      }
    },
  });

  const { isFetching, isLoading, refetch } = useQuery(
    ["admin_credit_report_list"],
    () =>
      ListCommanWay(
        "/admin/awbdata/creditreport",
        { ...query },
        { include: ["_invoice_no_c_b"] }
      ),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS" && data.data && Array.isArray(data.data) && data.data.length > 0) {
          console.log("data.data", data.data);
          setTableData(data.data);
          setSortStat({});

          // Set column keys dynamically from API response
          const keys = Object.keys(data.data[0]);
          setAllColumnKeys(keys);
          setVisibleColumns(loadSavedColumns(keys));

          let ary = [...data.data];
          let ttt = {
            base: 0, cgst: 0, sgst: 0, igst: 0, utgst: 0, grand: 0,
            ten_30: 0, holiday: 0, fuel: 0, chc: 0, weight: 0, diceWt: 0,
            ab: 0, dice: 0, dg: 0, pkg: 0, oda: 0, other: 0,
          };
          ary.forEach((a) => {
            console.log(a.base_value);
            ttt.base += Number(a["Basic Amount"] || 0);
            ttt.cgst += Number(a["CGST"] || 0);
            ttt.sgst += Number(a["SGST"] || 0);
            ttt.igst += Number(a["IGST"] || 0);
            ttt.grand += Number(a["Grand Total"] || 0);
            ttt.ten_30 += Number(a["10:30"] || 0);
            ttt.holiday += Number(a["Holiday"] || 0);
            ttt.fuel += Number(a["Fuel Surcharge"] || 0);
            ttt.chc += Number(a["CHC Charge"] || 0);
            ttt.weight += Number(a["weight"] || 0);
            ttt.diceWt += Number(a["D-Ice WT"] || 0);
            ttt.ab += Number(a["Awb charge"] || 0);
            ttt.dice += Number(a["Dry Ice"] || 0);
            ttt.dg += Number(a["DG charge"] || 0);
            ttt.pkg += Number(a["PKG charge"] || 0);
            ttt.oda += Number(a["ODA charge"] || 0);
            ttt.other += Number(a["Other charge"] || 0);
          });
          setTotals(ttt);
        } else {
          setTableData([]);
        }
      },
      onError: (data) => {
        setTableData([]);
      },
    }
  );

  useEffect(() => {
    setTotals({ grand: 0 });
    refetch();
  }, [query]);

  const { mutate: mutateRefreshRate } = useMutation(
    () =>
      ListCommanWay(
        "/admin/customer/refresh-rate",
        {
          startDate: startDate ? startDate : moment().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
          endDate: endDate ? endDate : moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
          customer: selectedCustomer?.value,
        },
        {}
      ),
    {
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          UseToast("Refresh Request Success", "success");
        } else {
          UseToast(data.message, "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  const RateRefresh = async () => {
    if (!selectedCustomer.value) return;
    Swal.fire({
      title: "Are you sure!",
      icon: "question",
      confirmButtonText: "Confirm",
      showCancelButton: true,
    }).then((x) => {
      if (x.isConfirmed) {
        mutateRefreshRate();
      }
    });
  };

  const __handleSubmit = async () => {
    setIsDataAdded(!isDataAdded);
    setQuery({
      date: {
        startDate: startDate ? startDate : moment().startOf("day").subtract(7, "days").format("YYYY-MM-DD HH:mm:ss"),
        endDate: endDate ? endDate : moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
      },
      customer: selectedCustomer?.value,
      product: selectedProduct?.value,
    });
  };

  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
  const [userDetails, setUserDetails] = useState<any>(userD?.state?.user || {});
  const tableRef = useRef(null);

  const exportToExcel = async () => {
    const table = tableRef.current;
    const tableClone = table.cloneNode(true);

    const tfoot = tableClone.getElementsByTagName("tfoot");
    if (tfoot.length > 0) tfoot[0].remove();
    const toIgnore = tableClone.querySelectorAll(".ignore-excel");
    if (toIgnore.length > 0) toIgnore[0].remove();

    // Find date column indices from header row
    const headerRow = tableClone.querySelectorAll("thead tr:last-child th");
    const dateColIndices: number[] = [];
    headerRow.forEach((th: any, idx: number) => {
      const text = th.innerText?.replace(/\n/g, " ").trim();
      if (text === "Date" || text === "Booking Date" || text === "Invoice Date") {
        dateColIndices.push(idx);
      }
    });

    const rows = tableClone.querySelectorAll("tbody tr");
    rows.forEach((row) => {
      dateColIndices.forEach((idx) => {
        const cell = row.cells[idx];
        if (cell && cell.innerText) {
          cell.innerText = moment(cell.innerText.trim(), "DD-MM-YYYY").format("YYYY-MM-DD");
        }
      });
    });

    const ws = utils.table_to_sheet(tableClone);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Sheet1");

    Object.keys(ws).forEach((key) => {
      if (key.startsWith("!")) return;
      let cell = ws[key];
      if (cell && cell.h && typeof cell.h === "string") cell.h = null;
      if (cell && cell.l) delete cell.l;
    });

    const wbout = write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "Credit Report.xlsx");
  };

  const GET_SORT_ICON = (key, info) => {
    switch (sortStat[key]) {
      case "asc":
        return <ArrowDownIcon className="h-4 inline-block" onClick={() => handleSort(key, info)} />;
      case "desc":
        return <ArrowUpIcon className="h-4 inline-block" onClick={() => handleSort(key, info)} />;
      default:
        return <ArrowsUpDownIcon className="h-4 inline-block" onClick={() => handleSort(key, info)} />;
    }
  };

  const handleSort = (key, info) => {
    if (!tableData || !Array.isArray(tableData) || tableData.length === 0) return;

    let data = [...tableData];
    let order = sortStat[key] == "asc" ? "desc" : "asc";

    if (key == "_invoice_no_c_b.isFullPaid") {
      data = data.sort((a, b) => {
        if (order == "asc") return -a["isActive"] + b["isActive"];
        else return -b["isActive"] + a["isActive"];
      });
    }

    let sortedData = data.sort((a, b) => {
      if (!a || !b) return 0;
      const valueA = key.split(".").reduce((obj, k) => obj?.[k], a);
      const valueB = key.split(".").reduce((obj, k) => obj?.[k], b);

      const isDateValue = (val: any) => typeof val === "string" && /^\d{2}-\d{2}-\d{4}$/.test(val);
      if (isDateValue(valueA) || isDateValue(valueB)) {
        const parseDate = (dateStr: string) => {
          if (!dateStr) return 0;
          const parts = dateStr.split("-");
          if (parts.length === 3) return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0])).getTime();
          return new Date(dateStr).getTime();
        };
        const dateA = parseDate(valueA);
        const dateB = parseDate(valueB);
        if (dateA < dateB) return order === "asc" ? -1 : 1;
        if (dateA > dateB) return order === "asc" ? 1 : -1;
        return 0;
      }

      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    });
    let _sortStat = { ...sortStat };
    _sortStat[key] = order;
    setSortStat(_sortStat);
    setTableData(sortedData);
  };

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter min-h-screen">
      {(isLoading || isFetching) && <Spinner />}

      {/* Popup Modal */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black-900 bg-opacity-50 z-[99] flex items-center justify-end p-4">
          <div className="rounded-2xl shadow-xl w-full max-w-5xl max-h-[95vh] overflow-y-auto relative bg-white">
            <button
              className="absolute top-3 right-3 text-slate-400 bg-white rounded-md hover:text-slate-700 z-10"
              onClick={() => setIsPopupOpen("")}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="pt-2">
              <ViewPickupMail pickup_code={isPopupOpen || ""} />
            </div>
          </div>
        </div>
      )}

      {/* Header Bar */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-between">
            {/* Total on Left */}
            <div className="bg-green-100 text-green-700 px-3 py-1 rounded-lg text-sm font-semibold">
              Total: {new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(Number(totals.grand || 0))}
            </div>

            {/* Centered Gradient Title */}
            <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">
              CREDIT REPORT
            </h1>

            {/* Columns Dropdown on Right */}
            {allColumnKeys.length > 0 ? (
              <Popover placement="bottom" isOpen={isColumnDropdownOpen} onOpenChange={setIsColumnDropdownOpen}>
                <PopoverTrigger>
                  <Button
                    size="sm"
                    variant="bordered"
                    className="h-8 bg-white/90 rounded-lg"
                    startContent={<AdjustmentsHorizontalIcon className="w-4 h-4" />}
                  >
                    {visibleColumns.length}/{allColumnKeys.length} Columns
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-2">
                  <div className="flex justify-between items-center pb-2 border-b mb-2">
                    <span className="text-xs font-semibold text-slate-600">Toggle Columns</span>
                    <Button size="sm" variant="flat" color="primary" className="h-6 text-xs px-2 ml-3" onPress={selectAllColumns}>
                      Select All
                    </Button>
                  </div>
                  <div className="flex flex-col gap-1 pr-5 max-h-96 overflow-y-auto">
                    {allColumnKeys.map((key, idx) => (
                      <div
                        key={key}
                        className="flex items-center gap-2 p-1 hover:bg-slate-100 rounded cursor-pointer"
                        onClick={() => toggleColumnVisibility(key)}
                      >
                        <span className="text-[10px] text-slate-400 w-4">{idx + 1}</span>
                        <Checkbox
                          size="sm"
                          isSelected={isColumnVisible(key)}
                          onValueChange={() => toggleColumnVisibility(key)}
                        />
                        <span className="text-xs text-slate-700">{key}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <div />
            )}
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl shadow-slate-200/50">
          <div className="flex flex-wrap items-end gap-3">
            {/* Customer Filter */}
            {(userDetails.role_name == 1 || userDetails.role_name == 10) && (
              <div className="min-w-[200px]">
                <label className="block mb-1 text-[11px] font-medium text-slate-600">Customer</label>
                <Select
                  styles={pickupSelectStyles}
                  options={customerList}
                  onChange={(e) => setBranchOtp(e)}
                  value={selectedCustomer}
                  isClearable={true}
                  placeholder="Select Customer"
                  menuPortalTarget={document.body}
                />
              </div>
            )}

            {/* Product Filter */}
            <div className="min-w-[160px]">
              <label className="block mb-1 text-[11px] font-medium text-slate-600">Product</label>
              <Select
                styles={pickupSelectStyles}
                options={productList}
                onChange={(e) => setSelectedProduct(e)}
                value={selectedProduct}
                isClearable={true}
                placeholder="All Products"
                menuPortalTarget={document.body}
              />
            </div>

            {/* Date Range */}
            <AfDateRangePicker setStartDate={setStartDate} setEndDate={setEndDate} label="Date Range" />

            {/* Action Buttons */}
            <Button
              size="sm"
              color="success"
              className="h-8 rounded-lg font-semibold"
              type="button"
              onPress={__handleSubmit}
              startContent={<MagnifyingGlassIcon className="w-4 h-4" />}
            >
              Search
            </Button>

            <Button
              size="sm"
              variant="flat"
              color="primary"
              className="h-8 rounded-lg font-semibold"
              startContent={<DocumentArrowDownIcon className="w-4 h-4" />}
              onPress={exportToExcel}
            >
              Export
            </Button>

            {userDetails.roleName === roles.SUPER_ADMIN && (
              <Button size="sm" color="danger" className="h-8 rounded-lg font-semibold" onPress={RateRefresh}>
                Rate Refresh
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className="mx-auto">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl shadow-slate-200/50 overflow-hidden">
          {/* Table Content */}
          <div className="overflow-x-auto">
            {!isLoading && !isFetching && (!tableData || !Array.isArray(tableData) || tableData.length === 0) && (
              <div className="flex flex-col items-center py-12">
                <svg className="w-16 h-16 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <p className="text-slate-500 font-medium">No data found</p>
                <p className="text-slate-400 text-sm">Try adjusting your filters</p>
              </div>
            )}

            {tableData && tableData.length && (
              <table className="w-full border-collapse" ref={tableRef} id="myTable" style={{ fontSize: "85%" }}>
                <thead className="sticky top-0 z-10 bg-slate-100">
                  <tr className="ignore-excel">
                    <th
                      className="border border-slate-200 px-2 bg-slate-100 text-left text-xs font-bold text-slate-700"
                      colSpan={1 + allColumnKeys.slice(0, allColumnKeys.indexOf("weight")).filter(k => isColumnVisible(k)).length}
                    >
                      Results &nbsp;|&nbsp; <span className="text-primary font-semibold">{tableData.length} entries</span>
                    </th>
                    {isColumnVisible("weight") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.weight?.toFixed(2)}</th>}
                    {isColumnVisible("D-Ice WT") && <th className="border border-slate-200 px-1 bg-slate-100 min-w-[50px]">{totals.diceWt?.toFixed(2)}</th>}
                    {isColumnVisible("Basic Amount") && <th className="border border-slate-200 px-1 py-2 bg-slate-100">{totals.base?.toFixed(2)}</th>}
                    {isColumnVisible("Holiday") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.holiday?.toFixed(2)}</th>}
                    {isColumnVisible("10:30") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.ten_30?.toFixed(2)}</th>}
                    {isColumnVisible("Fuel Surcharge") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.fuel?.toFixed(2)}</th>}
                    {isColumnVisible("CHC Charge") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.chc?.toFixed(2)}</th>}
                    {isColumnVisible("Awb charge") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.ab?.toFixed(2)}</th>}
                    {isColumnVisible("Dry Ice") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.dice?.toFixed(2)}</th>}
                    {isColumnVisible("DG charge") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.dg?.toFixed(2)}</th>}
                    {isColumnVisible("PKG charge") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.pkg?.toFixed(2)}</th>}
                    {isColumnVisible("ODA charge") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.oda?.toFixed(2)}</th>}
                    {isColumnVisible("Other charge") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.other?.toFixed(2)}</th>}
                    {isColumnVisible("CGST") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.cgst?.toFixed(2)}</th>}
                    {isColumnVisible("SGST") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.sgst?.toFixed(2)}</th>}
                    {isColumnVisible("IGST") && <th className="border border-slate-200 px-1 bg-slate-100">{totals.igst?.toFixed(2)}</th>}
                    {isColumnVisible("Grand Total") && <th className="border border-slate-200 px-1 bg-slate-100">{Number(totals.grand).toFixed(2)}</th>}
                    {isColumnVisible("Invoice No") && <th className="border border-slate-200 px-1 bg-slate-100"></th>}
                    {isColumnVisible("Invoice Date") && <th className="border border-slate-200 px-1 bg-slate-100"></th>}
                    {isColumnVisible("Status") && <th className="border border-slate-200 px-1 bg-slate-100"></th>}
                  </tr>
                  <tr>
                    <th className="border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase bg-slate-200">Sr.</th>
                    {Object.keys(tableData[0]).map((h) => {
                      if (!isColumnVisible(h)) return null;
                      const isDateCol = h === "Date" || h === "Booking Date" || h === "Invoice Date";
                      return (
                        <th
                          key={"header-" + h}
                          className={`border border-slate-200 px-1 text-[10px] font-semibold text-slate-600 uppercase cursor-pointer hover:bg-slate-300 bg-slate-200${isDateCol ? " whitespace-nowrap" : ""}`}
                          onClick={() => handleSort(h, "asc")}
                        >
                          <span dangerouslySetInnerHTML={{ __html: isDateCol ? h : h.replace(/ /g, "<br/>") }} /> {GET_SORT_ICON(h, "asc")}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((r, i) => {
                    return (
                      <tr key={"r" + r["AWB"]} className="hover:bg-slate-50 transition-colors">
                        <td className="border border-slate-200 px-2">{i + 1}</td>
                        {Object.keys(r).map((h) => {
                          if (!isColumnVisible(h)) return null;
                          if (h === "AWB") {
                            return (
                              <td key={"body-" + i + h} className="border border-slate-200 px-1 whitespace-nowrap">
                                <a target="_blank" href={`/pod/tracking?awb=${r[h]}`} className="text-primary2">
                                  {r[h]}
                                </a>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    await navigator.clipboard.writeText(r[h] || "-");
                                    UseToast("Copied!");
                                  }}
                                  className="ml-2"
                                >
                                  <DocumentDuplicateIcon className="h-5 mr-2" />
                                </button>
                                {r["Pickup Code"] && (
                                  <button
                                    type="button"
                                    onClick={() => setIsPopupOpen(r["Pickup Code"] || "")}
                                    className=""
                                  >
                                    <EnvelopeIcon className="h-5 mr-2" />
                                  </button>
                                )}
                              </td>
                            );
                          } else {
                            return (
                              <td key={"body-" + i + h} className="border border-slate-200 px-1">
                                {r[h]}
                              </td>
                            );
                          }
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditReport;
